<template>
  <uploader
    @files-added="filesAdded"
    @file-progress="fileProgress"
    @complete="complete"
    :options="uploadOptions"
    @drop="onDrop($event)"
    ref="uploader"
    :class="getClass()"
  >
    <uploader-btn
      v-if="size === 'large'"
      :single="false"
      :attrs="uploadAttrs"
      >{{ title }}</uploader-btn
    >

    <uploader-btn v-else class="add" :single="false" :attrs="uploadAttrs">
      nieuw product
    </uploader-btn>
  </uploader>
</template>

<script>
import AddProduct from './dialogs/AddProduct.vue'
import { EventBus, Events } from '@/eventbus/EventBus'
import ErrorMessage from '@/dialogs/ErrorMessage.vue'
import { uuidv4 } from '@/api/uuid'
import { mapStores } from 'pinia'
import { usePrintStoreStore } from '@/features/printstore/PrintStore.store'

export default {
  name: 'PrintStoreProductUpload',
  props: {
    collectionId: {
      type: String,
      required: true
    },
    title: {
      type: String,
      required: false
    },
    size: {
      type: String,
      default: 'large'
    }
  },
  computed: {
    ...mapStores(usePrintStoreStore)
  },
  mounted() {
    this.uploader = this.$refs.uploader.uploader
    this.uploader.assignDrop(document.getElementById('drop-zone'))
  },
  data() {
    return {
      files: [],
      uploadOptions: {
        // TODO: should call /api/v3 eventually
        target: '/api/v2/print-store/file',
        testChunks: true,
        autoStart: false,
        initialPaused: true,
        query: (file) => {
          return {
            isPreview: false,
            fileType: 'SOURCE',
            productId: this.getProductIdForFile(file),
            collectionId: this.collectionId
          }
        },
        progressCallbacksInterval: 0,
        chunkSize: 524288, // 0.5MB
        generateUniqueIdentifier: () => {
          return uuidv4()
        },
        permanentErrors: [404, 415, 422, 500, 501, 409]
      },
      uploadAttrs: {
        accept: ['application/pdf', 'image/jpeg']
      },
      uploader: {},
      productIdMap: undefined,
      progress: 0
    }
  },
  methods: {
    getClass() {
      return {
        small: this.size === 'small',
        large: this.size === 'large'
      }
    },
    onDrop(event) {
      event.stopPropagation()
    },
    filesAdded(files) {
      files.forEach((item) => {
        if (!this.uploadAttrs.accept.includes(item.file.type)) {
          this.$modal.show(
            ErrorMessage,
            {
              title: 'Ongeldig bestandsformaat',
              text: 'Alleen PDF en JPG bestanden zijn toegestaan.'
            },
            {
              height: 'auto',
              width: 380,
              adaptive: true
            }
          )
          item.cancel()
        } else {
          item.pause()
          this.files.push(item)
          this.printStoreStore.addFile({
            id: item.uniqueIdentifier,
            name: item.name
          })
        }
      })

      if (this.files.length > 0) {
        this.openProductDialog()
      }
    },
    fileProgress(rootFile, file, chunk) {
      this.printStoreStore.uploadProgress(this.uploader.progress())
      this.printStoreStore.fileProgress({
        id: file.uniqueIdentifier,
        progress: file.progress()
      })
    },
    complete() {
      if (this.files.length > 0) {
        this.files = []
        EventBus.emit(Events.PRINT_STORE_UPLOAD_COMPLETE, this.collectionId)
        this.printStoreStore.complete()
      }
    },
    openProductDialog() {
      this.$modal.show(
        AddProduct,
        {
          files: this.files,
          collectionId: this.collectionId,
          callback: (productIdMap) => {
            this.productIdMap = productIdMap
            this.files.forEach((file) => file.resume())
          }
        },
        {
          adaptive: true,
          width: 380,
          clickToClose: false
        },
        {
          'before-close': () => {
            this.files = []
            this.uploader.cancel()
          }
        }
      )
    },
    // Product IDs are added to the file in the Product dialog
    getProductIdForFile(file) {
      return this.productIdMap.get(file.uniqueIdentifier)
    }
  }
}
</script>

<style scoped lang="scss">
@use '../../sass/helpers/_variables.scss' as *;
@use '../../sass/helpers/_mixins.scss' as *;
@use '../../sass/base/_colors.scss' as *;
@use '../../sass/assets/_buttons.scss' as *;

// Styling the upload button as a pill-button
.large {
  .uploader-btn {
    @extend .pill-button;
    border: none;
    overflow: hidden;
    margin: 0;

    &:hover {
      background: var(--sub-color);
    }
  }
}

.small {
  border-radius: 50px;

  .add {
    color: var(--sub-color);
    border: none;
    text-transform: uppercase;
    font-size: 11px;
    padding: 0;
    margin: 0;

    &:hover {
      background: transparent;
    }
  }
}
</style>
